import { ID,Response } from "../../../../../../_metronic/helpers"
import { Client } from "../../../../clients/components/core/_models"
import { Project } from "../../../../project/components/core/_models"

export type ContractAiAnalysisSummary = {
  status: 'queued' | 'processing' | 'completed' | 'failed'
  analysis_id?: number
  result_status?: string | null
  has_suggestions?: boolean
  items_count?: number
  message?: string | null
  completed_at?: string | null
}

export type ContractRushmindProgress = {
  status: 'queued' | 'processing'
  analysis_id: number
  queue_position: number
  queue_total: number
  queue_ahead: number
  estimated_processing_seconds: number
  estimated_wait_seconds?: number
  estimated_remaining_seconds?: number
  estimated_wait_label: string
  queued_at?: string | null
  started_at?: string | null
}

export type Contracts = {
  id?: ID
  name_contracts?: string
  client_id?: number
  title?: string
  project_id?: number
  client?: Client
  project?: Project
  size?: number
  last_modified?: string
  begin_date?: string
  end_date?: string
  type?: number
  value?: string
  file?: File | string
  expire?: string
  obs?: string
  ai_analysis?: ContractAiAnalysisSummary | null
  /** Enviado apenas no upload; quando false, contrato é salvo sem fila do RushMind. */
  analyze_with_rushmind?: boolean
}

export type ContractAiStatus =
  | 'success'
  | 'skipped_too_large'
  | 'skipped_unsupported'
  | 'skipped_no_text'
  | 'skipped_rate_limited'
  | 'no_suggestions'
  | 'unavailable'

export type ContractRushmindEstimate = {
  queue_ahead: number
  estimated_char_count: number
  estimated_chunks: number
  estimated_processing_seconds: number
  estimated_wait_seconds: number
  estimated_wait_label: string
}

export type ContractAiQueuedResponse = {
  status: 'queued'
  message: string
  poll_url: string
  poll_interval_ms: number
  analysis_id?: number
  estimate?: ContractRushmindEstimate
}

export type ContractAiPollStatus = 'queued' | 'processing' | 'completed' | 'failed'

export type ContractAiAnalysisStatusResponse = {
  status: ContractAiPollStatus
  started_at?: string
  completed_at?: string
  result?: ContractAiResult
}

/** Item unificado retornado pelo RushMind no contrato (pode ser tarefa, fase ou ambos). */
export type ContractSuggestedItem = {
  id: string
  title: string
  description?: string | null
  expected_date?: string | null
  begin_date?: string | null
  end_date?: string | null
  is_task: boolean
  is_phase: boolean
}

/** @deprecated Use ContractSuggestedItem — mantido para compatibilidade interna */
export type ContractSuggestedTask = Pick<
  ContractSuggestedItem,
  'id' | 'title' | 'description' | 'expected_date'
>

/** @deprecated Use ContractSuggestedItem */
export type ContractSuggestedPhase = {
  id: string
  name: string
  begin_date: string
  end_date: string
}

export type ContractAiResult = {
  status: ContractAiStatus
  message?: string | null
  suggestions: {
    items: ContractSuggestedItem[]
  }
}

export type ResponseContract = {
  contracts: Contracts | Contracts[]
  message: string
  errors?: boolean
  ai?: ContractAiResult | ContractAiQueuedResponse | null
}

export function isContractAiQueued(
  ai: ContractAiResult | ContractAiQueuedResponse | null | undefined
): ai is ContractAiQueuedResponse {
  return ai != null && ai.status === 'queued'
}

export function getCreatedContractId(response: ResponseContract): number | null {
  const created = contractFromResponse(response)
  const id = created?.id
  if (id === undefined || id === null) {
    return null
  }
  return Number.isFinite(id) ? id : null
}

export type CreateSuggestedTasksPayload = {
  tasks: Array<{
    title: string
    description?: string | null
    expected_date?: string | null
  }>
}

export type CreateSuggestedTasksResponse = {
  message: string
  created: number
}

export type CreateSuggestedSchedulePhasesPayload = {
  phases: Array<{
    name: string
    begin_date: string
    end_date: string
  }>
}

export type CreateSuggestedSchedulePhasesResponse = {
  message: string
  created: number
  schedule_id?: number
}

export type ContractsQueryResponse = Response<Array<Contracts>>

export const initialContract: Contracts = {
  id: undefined,
  name_contracts: '',
  client_id: 1,
  value: '0'
}

export function contractFileUrl(file: Contracts['file']): string | undefined {
  return typeof file === 'string' ? file : undefined
}

export function contractFromResponse(response: ResponseContract): Contracts {
  const payload = response.contracts
  if (Array.isArray(payload)) {
    return payload[0] ?? initialContract
  }
  return payload ?? initialContract
}
